// S35 帳務總帳(/accounting)— Accounting(IA specs/01 §2.3)。
// ledger drill-down(帳戶分錄 → 交易 → 分錄/沖銷鏈)+ 受控 credit / refund /
// adjustment 表單(reversal+replacement,不 UPDATE/DELETE;reason/case 必填、
// 二段確認、durable maker/checker + password step-up)。UI 不顯示完整 payment secret——gateway 參照僅
// provider id。客戶 statement→invoice→lines 的 admin 瀏覽端點待落地(誠實標示);
// B2B invoice 鏡像在 /companies(S17),ledger 真值在本頁。

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { ApiError } from '../../lib/api';
import { ErrorState } from '../../components/StateBlock';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import { formatTimestampUs } from '../../lib/format';
import { roleLabel } from '../users/userModel';
import '../_shared/shared.css';
import './commerce.css';
import type { LedgerEntry, LedgerTransaction } from './commerceApi';
import { getLedgerTransaction, listLedgerEntries, requestCommerceOperation } from './commerceApi';
import { CommerceApprovalPanel } from './CommerceApprovalPanel';
import {
  DUAL_CONTROL_NOTE,
  KNOWN_LEDGER_ACCOUNTS,
  adjustmentBalance,
  canCommerceWrite,
  formatMinor,
  ledgerDirectionLabel,
  usFromLocalInput,
} from './commerceModel';

function errorText(e: unknown): string {
  if (e instanceof ApiError) return `${e.message}${e.requestId ? `（request_id: ${e.requestId}）` : ''}`;
  return e instanceof Error ? e.message : '操作失敗';
}

interface AdjEntryRow {
  billing_account_id: string;
  account: string;
  direction: string;
  amount: string;
}

const EMPTY_ADJ_ROW: AdjEntryRow = { billing_account_id: '', account: '', direction: 'debit', amount: '0' };

export default function AccountingPage() {
  const { role } = useAuth();
  const writable = canCommerceWrite(role);
  const [searchParams, setSearchParams] = useSearchParams();

  // 帳戶分錄瀏覽
  const [accountInput, setAccountInput] = useState('');
  const [queriedAccount, setQueriedAccount] = useState('');
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [entriesErr, setEntriesErr] = useState<unknown>(null);

  // 交易明細
  const [txInput, setTxInput] = useState('');
  const [tx, setTx] = useState<LedgerTransaction | null>(null);
  const [txLoading, setTxLoading] = useState(false);
  const [txErr, setTxErr] = useState<unknown>(null);

  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [err, setErr] = useState('');
  const [latestOperationId, setLatestOperationId] = useState('');

  // credit 表單
  const [creditAccount, setCreditAccount] = useState('');
  const [creditType, setCreditType] = useState('goodwill');
  const [creditAmount, setCreditAmount] = useState('0');
  const [creditCurrency, setCreditCurrency] = useState('USD');
  const [creditExpires, setCreditExpires] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [creditCaseId, setCreditCaseId] = useState('');
  const [confirmCredit, setConfirmCredit] = useState(false);

  // refund 表單
  const [refundAccount, setRefundAccount] = useState('');
  const [refundPayment, setRefundPayment] = useState('');
  const [refundAmount, setRefundAmount] = useState('0');
  const [refundReason, setRefundReason] = useState('');
  const [refundCaseId, setRefundCaseId] = useState('');
  const [confirmRefund, setConfirmRefund] = useState(false);

  // adjustment 表單
  const [adjCurrency, setAdjCurrency] = useState('USD');
  const [adjDescription, setAdjDescription] = useState('');
  const [adjRows, setAdjRows] = useState<AdjEntryRow[]>([{ ...EMPTY_ADJ_ROW }, { ...EMPTY_ADJ_ROW, direction: 'credit' }]);
  const [adjReason, setAdjReason] = useState('');
  const [adjCaseId, setAdjCaseId] = useState('');
  const [confirmAdj, setConfirmAdj] = useState(false);

  async function loadTx(id: string, writeUrl = true) {
    if (!id.trim()) return;
    setTxLoading(true);
    setTxErr(null);
    try {
      const loaded = await getLedgerTransaction(id.trim());
      setTx(loaded);
      setTxInput(id.trim());
      if (writeUrl) setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tx', id.trim());
        return next;
      }, { replace: true });
    } catch (e) {
      setTxErr(e);
      setTx(null);
    } finally {
      setTxLoading(false);
    }
  }

  async function loadEntries(account: string) {
    if (!account.trim()) return;
    setEntriesLoading(true);
    setEntriesErr(null);
    try {
      const rows = await listLedgerEntries(account.trim());
      setEntries(rows);
      setQueriedAccount(account.trim());
    } catch (e) {
      setEntriesErr(e);
      setEntries([]);
      setQueriedAccount(account.trim());
    } finally {
      setEntriesLoading(false);
    }
  }

  useEffect(() => {
    const fromUrl = searchParams.get('tx');
    if (fromUrl) void loadTx(fromUrl, false);
    const acc = searchParams.get('account');
    if (acc) {
      setAccountInput(acc);
      void loadEntries(acc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const entryTotals = useMemo(() => {
    let debit = 0;
    let credit = 0;
    for (const e of entries) {
      if (e.direction === 'debit') debit += e.amount_minor;
      else credit += e.amount_minor;
    }
    return { debit, credit };
  }, [entries]);

  const txBalance = useMemo(() => {
    if (!tx) return null;
    return adjustmentBalance(tx.entries.map((e) => ({ direction: e.direction, amount_minor: e.amount_minor })));
  }, [tx]);

  const creditAmountMinor = Number.parseInt(creditAmount, 10);
  const creditReady = Boolean(
    writable &&
      creditAccount.trim() &&
      creditType.trim() &&
      Number.isFinite(creditAmountMinor) &&
      creditAmountMinor > 0 &&
      /^[A-Za-z]{3}$/.test(creditCurrency.trim()) &&
      creditReason.trim() &&
      creditCaseId.trim()
  );
  const refundAmountMinor = Number.parseInt(refundAmount, 10);
  const refundReady = Boolean(
    writable &&
      refundAccount.trim() &&
      refundPayment.trim() &&
      Number.isFinite(refundAmountMinor) &&
      refundAmountMinor > 0 &&
      refundReason.trim() &&
      refundCaseId.trim()
  );
  const adjEntries = useMemo(
    () =>
      adjRows.map((r) => ({
        billing_account_id: r.billing_account_id.trim() || undefined,
        account: r.account.trim(),
        direction: r.direction,
        amount_minor: Number.parseInt(r.amount, 10),
      })),
    [adjRows]
  );
  const adjBalance = useMemo(
    () => adjustmentBalance(adjEntries.map((e) => ({ direction: e.direction, amount_minor: e.amount_minor }))),
    [adjEntries]
  );
  const adjReady = Boolean(
    writable &&
      /^[A-Za-z]{3}$/.test(adjCurrency.trim()) &&
      adjDescription.trim() &&
      adjEntries.every((e) => e.account && Number.isFinite(e.amount_minor) && e.amount_minor >= 0) &&
      adjBalance.balanced &&
      adjReason.trim() &&
      adjCaseId.trim()
  );

  async function submitCredit() {
    setBusy(true);
    try {
      const op = await requestCommerceOperation('credit.create', {
        billing_account_id: creditAccount.trim(),
        type: creditType.trim(),
        amount_minor: creditAmountMinor,
        currency: creditCurrency.trim().toUpperCase(),
        expires_at: usFromLocalInput(creditExpires),
      }, {
        reason: creditReason.trim(),
        case_id: creditCaseId.trim(),
      });
      setConfirmCredit(false);
      setErr('');
      setLatestOperationId(op.operation_id);
      setNotice(`已建立 credit 覆核單 ${op.operation_id}；尚未入帳。`);
      setCreditReason('');
      setCreditCaseId('');
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitRefund() {
    setBusy(true);
    try {
      const op = await requestCommerceOperation('refund.create', {
        billing_account_id: refundAccount.trim(),
        payment_id: refundPayment.trim(),
        amount_minor: refundAmountMinor,
      }, {
        reason: refundReason.trim(),
        case_id: refundCaseId.trim(),
      });
      setConfirmRefund(false);
      setErr('');
      setLatestOperationId(op.operation_id);
      setNotice(`已建立退款覆核單 ${op.operation_id}；尚未呼叫金流閘道。`);
      setRefundReason('');
      setRefundCaseId('');
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitAdjustment() {
    setBusy(true);
    try {
      const op = await requestCommerceOperation('adjustment.post', {
        currency: adjCurrency.trim().toUpperCase(),
        description: adjDescription.trim(),
        entries: adjEntries.map((e) => ({
          billing_account_id: e.billing_account_id,
          account: e.account,
          direction: e.direction,
          amount_minor: e.amount_minor,
        })),
      }, {
        reason: adjReason.trim(),
        case_id: adjCaseId.trim(),
      });
      setConfirmAdj(false);
      setErr('');
      setLatestOperationId(op.operation_id);
      setNotice(`已建立 adjustment 覆核單 ${op.operation_id}；尚未寫入 ledger。`);
      setAdjReason('');
      setAdjCaseId('');
      setAdjDescription('');
      setAdjRows([{ ...EMPTY_ADJ_ROW }, { ...EMPTY_ADJ_ROW, direction: 'credit' }]);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  const anyConfirmOpen = confirmCredit || confirmRefund || confirmAdj;

  return (
    <div className="commerce-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">帳務總帳</h1>
          <p className="page-subtitle">總帳帳戶／交易明細與受控的開立額度、退款、調帳;帳本僅可追加,更正一律反向分錄。</p>
        </div>
        <div className="adm-actionbar-btns">
          <span className={`pill ${writable ? 'pill-good' : 'pill-neutral'}`}>{role ? roleLabel(role) : '其他角色'}</span>
        </div>
      </div>

      {notice && (
        <p className="adm-action-result is-ok" role="status">
          {notice}
        </p>
      )}
      {err && !anyConfirmOpen && (
        <p className="adm-action-result is-err" role="alert">
          {err}
        </p>
      )}

      <p className="commerce-gap-note">
        drill-down 鏈現況:ledger 帳戶 → 交易 → 分錄(本頁)已接真;交易的 source 參照(invoice / payment / refund / credit / plan_grant)
        以 source id 呈現。客戶 statement→invoice→lines 的 admin 瀏覽端點待落地(現有 `/v3/billing/statement` 僅限本人);B2B 出貨
        invoice 鏡像請至 <Link to="/companies">B2B 公司</Link>。金流 secret / 卡號等不進本主控台,僅顯示 provider 參照 id。
      </p>

      <div className="commerce-split">
        <div className="commerce-side" style={{ minWidth: 0 }}>
          <section className="card">
            <div className="section-head">
              <div>
                <h2 className="section-title">帳戶分錄瀏覽</h2>
                <span className="section-meta adm-mono">{queriedAccount || '尚未查詢'}</span>
              </div>
              {entriesLoading && <span className="pill pill-neutral">查詢中</span>}
            </div>
            <div className="commerce-query-row" style={{ marginBottom: 'var(--spacing-6)' }}>
              <label className="adm-field" style={{ flex: 1 }}>
                <span className="adm-field-label text-secondary">總帳帳戶</span>
                <input
                  value={accountInput}
                  onChange={(e) => setAccountInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void loadEntries(accountInput);
                  }}
                  list="ledger-accounts"
                  placeholder="expense:plan_grants"
                />
              </label>
              <button className="btn-primary" disabled={entriesLoading || !accountInput.trim()} onClick={() => void loadEntries(accountInput)}>
                查詢
              </button>
            </div>
            <datalist id="ledger-accounts">
              {KNOWN_LEDGER_ACCOUNTS.map((a) => (
                <option key={a} value={a} />
              ))}
            </datalist>
            {entriesErr ? (
              <ErrorState error={entriesErr} onRetry={() => void loadEntries(accountInput)} />
            ) : queriedAccount ? (
              entries.length > 0 ? (
                <>
                  <div className="commerce-balance" style={{ marginBottom: 'var(--spacing-4)' }}>
                    <span>
                      借方合計:<span className="adm-mono">{entryTotals.debit}</span>
                    </span>
                    <span>
                      貸方合計:<span className="adm-mono">{entryTotals.credit}</span>
                    </span>
                    <span className="text-tertiary">最新 {entries.length} 筆(上限 500;僅此帳戶單邊分錄,借貸不必互抵)</span>
                  </div>
                  <div className="table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>時間</th>
                          <th>方向</th>
                          <th>金額</th>
                          <th>帳務帳戶</th>
                          <th>交易</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map((e) => (
                          <tr key={e.entry_id} className="clickable" onClick={() => void loadTx(e.transaction_id)}>
                            <td className="adm-mono">{formatTimestampUs(e.occurred_at)}</td>
                            <td>
                              <span className={`pill ${e.direction === 'debit' ? 'pill-info' : 'pill-neutral'}`}>{ledgerDirectionLabel(e.direction)}</span>
                            </td>
                            <td className="adm-mono">{formatMinor(e.amount_minor, e.currency)}</td>
                            <td className="adm-mono">{e.billing_account_id || '—'}</td>
                            <td className="adm-mono" title={e.transaction_id}>
                              {e.transaction_id.slice(0, 16)}…
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">📒</div>
                  <div className="empty-state-title">此帳戶目前沒有分錄</div>
                  <div className="empty-state-detail">確認帳戶名(格式 kind:name),或改查其他帳戶。</div>
                </div>
              )
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">📒</div>
                <div className="empty-state-title">輸入 ledger 帳戶開始瀏覽</div>
                <div className="empty-state-detail">帳戶以 GSI 為查詢鍵;常用帳戶已提供於輸入建議。</div>
              </div>
            )}
          </section>

          <section className="card">
            <div className="section-head">
              <div>
                <h2 className="section-title">交易明細</h2>
                <span className="section-meta adm-mono">{tx?.transaction_id || '尚未載入'}</span>
              </div>
              {txLoading && <span className="pill pill-neutral">查詢中</span>}
            </div>
            <div className="commerce-query-row" style={{ marginBottom: 'var(--spacing-6)' }}>
              <label className="adm-field" style={{ flex: 1 }}>
                <span className="adm-field-label text-secondary">交易 ID</span>
                <input
                  value={txInput}
                  onChange={(e) => setTxInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void loadTx(txInput);
                  }}
                  placeholder="ltx-…"
                />
              </label>
              <button className="btn-primary" disabled={txLoading || !txInput.trim()} onClick={() => void loadTx(txInput)}>
                載入
              </button>
            </div>
            {txErr ? (
              <ErrorState error={txErr} onRetry={() => void loadTx(txInput)} />
            ) : tx ? (
              <div className="commerce-form">
                <div className="commerce-tx-meta">
                  <div className="commerce-ent-item">
                    <span className="commerce-ent-label">來源</span>
                    <span className="commerce-ent-value adm-mono">
                      {tx.source_type}:{tx.source_id}
                    </span>
                  </div>
                  <div className="commerce-ent-item">
                    <span className="commerce-ent-label">描述</span>
                    <span className="commerce-ent-value">{tx.description || '—'}</span>
                  </div>
                  <div className="commerce-ent-item">
                    <span className="commerce-ent-label">發生時間</span>
                    <span className="commerce-ent-value adm-mono">{formatTimestampUs(tx.occurred_at)}</span>
                  </div>
                  <div className="commerce-ent-item">
                    <span className="commerce-ent-label">操作者 / 案件</span>
                    <span className="commerce-ent-value adm-mono">
                      {tx.created_by || '—'}
                      {tx.case_id ? ` / ${tx.case_id}` : ''}
                    </span>
                  </div>
                  <div className="commerce-ent-item">
                    <span className="commerce-ent-label">沖銷對象</span>
                    <span className="commerce-ent-value">
                      {tx.reversal_of ? (
                        <button className="btn-ghost btn-sm" onClick={() => void loadTx(tx.reversal_of!)}>
                          <span className="adm-mono">{tx.reversal_of.slice(0, 16)}…</span>
                        </button>
                      ) : (
                        '—'
                      )}
                    </span>
                  </div>
                  <div className="commerce-ent-item">
                    <span className="commerce-ent-label">借貸平衡</span>
                    <span className="commerce-ent-value">
                      {txBalance ? (
                        <span className={`pill ${txBalance.balanced ? 'pill-good' : 'pill-danger'}`}>
                          {txBalance.balanced ? '平衡' : '不平衡'}(借 {txBalance.debit} / 貸 {txBalance.credit})
                        </span>
                      ) : (
                        '—'
                      )}
                    </span>
                  </div>
                </div>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>帳戶</th>
                        <th>方向</th>
                        <th>金額</th>
                        <th>帳務帳戶</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tx.entries.map((e) => (
                        <tr key={e.entry_id}>
                          <td>
                            <button
                              className="btn-ghost btn-sm"
                              onClick={() => {
                                setAccountInput(e.account);
                                void loadEntries(e.account);
                              }}
                              title="瀏覽此帳戶分錄"
                            >
                              <span className="adm-mono">{e.account}</span>
                            </button>
                          </td>
                          <td>
                            <span className={`pill ${e.direction === 'debit' ? 'pill-info' : 'pill-neutral'}`}>{ledgerDirectionLabel(e.direction)}</span>
                          </td>
                          <td className="adm-mono">{formatMinor(e.amount_minor, e.currency)}</td>
                          <td className="adm-mono">{e.billing_account_id || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">🧾</div>
                <div className="empty-state-title">輸入交易 ID,或自分錄列 / grant / 對帳單點入</div>
                <div className="empty-state-detail">grant、credit、refund、adjustment 的回執都帶 ledger 交易 ID,可直接深連到此。</div>
              </div>
            )}
          </section>
        </div>

        <aside className="commerce-side">
          <section className="card card--primary">
            <div className="section-head">
              <h2 className="section-title">{writable ? '開立 credit' : '唯讀檢視'}</h2>
            </div>
            {writable ? (
              <div className="commerce-form">
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">帳務帳戶</span>
                  <input value={creditAccount} onChange={(e) => setCreditAccount(e.target.value)} placeholder="ba-…" disabled={busy} />
                </label>
                <div className="commerce-form-grid">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">類型</span>
                    <input value={creditType} onChange={(e) => setCreditType(e.target.value)} placeholder="goodwill / service_credit" disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">到期(留空=不到期)</span>
                    <input type="datetime-local" value={creditExpires} onChange={(e) => setCreditExpires(e.target.value)} disabled={busy} />
                  </label>
                </div>
                <div className="commerce-form-grid">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">金額(minor units)</span>
                    <input value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} inputMode="numeric" disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">幣別(需同帳戶)</span>
                    <input value={creditCurrency} onChange={(e) => setCreditCurrency(e.target.value)} placeholder="USD" disabled={busy} />
                  </label>
                </div>
                <div className="commerce-form-grid">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">原因</span>
                    <input value={creditReason} onChange={(e) => setCreditReason(e.target.value)} disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">案件編號</span>
                    <input value={creditCaseId} onChange={(e) => setCreditCaseId(e.target.value)} disabled={busy} />
                  </label>
                </div>
                <button className="btn-primary" disabled={busy || !creditReady} onClick={() => { setErr(''); setConfirmCredit(true); }}>
                  開立 credit
                </button>
              </div>
            ) : (
              <p className="commerce-hint text-tertiary">目前角色僅能檢視 ledger;credit / refund / adjustment 由 admin / platform-owner 執行。</p>
            )}
          </section>

          {writable && (
            <section className="card card--danger">
              <div className="section-head">
                <h2 className="section-title">退款(金流方實退)</h2>
              </div>
              <div className="commerce-form">
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">帳務帳戶</span>
                  <input value={refundAccount} onChange={(e) => setRefundAccount(e.target.value)} placeholder="ba-…" disabled={busy} />
                </label>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">付款 ID</span>
                  <input value={refundPayment} onChange={(e) => setRefundPayment(e.target.value)} placeholder="pay-…" disabled={busy} />
                </label>
                <div className="commerce-form-grid">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">退款金額(minor units)</span>
                    <input value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} inputMode="numeric" disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">案件編號</span>
                    <input value={refundCaseId} onChange={(e) => setRefundCaseId(e.target.value)} disabled={busy} />
                  </label>
                </div>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">原因</span>
                  <input value={refundReason} onChange={(e) => setRefundReason(e.target.value)} disabled={busy} />
                </label>
                <p className="commerce-dc-note">{DUAL_CONTROL_NOTE}</p>
                <button className="btn-danger" disabled={busy || !refundReady} onClick={() => { setErr(''); setConfirmRefund(true); }}>
                  送出退款
                </button>
                <span className="commerce-hint text-tertiary">
                  超過原付款可退餘額後端會擋;同案件重送為冪等重放,不會重複退。
                </span>
              </div>
            </section>
          )}

          {writable && (
            <section className="card card--muted">
              <div className="section-head">
                <h2 className="section-title">受控調帳</h2>
              </div>
              <div className="commerce-form">
                <div className="commerce-form-grid">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">幣別</span>
                    <input value={adjCurrency} onChange={(e) => setAdjCurrency(e.target.value)} placeholder="USD" disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">描述</span>
                    <input value={adjDescription} onChange={(e) => setAdjDescription(e.target.value)} disabled={busy} />
                  </label>
                </div>
                <div className="commerce-rows-editor">
                  {adjRows.map((row, i) => (
                    <div className="commerce-row-line commerce-row-line--entry" key={i}>
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">帳戶</span>
                        <input value={row.account} list="ledger-accounts" onChange={(e) => setAdjRows((rows) => rows.map((r, j) => (j === i ? { ...r, account: e.target.value } : r)))} disabled={busy} />
                      </label>
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">方向</span>
                        <select value={row.direction} onChange={(e) => setAdjRows((rows) => rows.map((r, j) => (j === i ? { ...r, direction: e.target.value } : r)))} disabled={busy}>
                          <option value="debit">借</option>
                          <option value="credit">貸</option>
                        </select>
                      </label>
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">金額</span>
                        <input value={row.amount} inputMode="numeric" onChange={(e) => setAdjRows((rows) => rows.map((r, j) => (j === i ? { ...r, amount: e.target.value } : r)))} disabled={busy} />
                      </label>
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">帳務帳戶(選填)</span>
                        <input value={row.billing_account_id} onChange={(e) => setAdjRows((rows) => rows.map((r, j) => (j === i ? { ...r, billing_account_id: e.target.value } : r)))} disabled={busy} />
                      </label>
                      <button className="btn-ghost btn-sm" disabled={busy || adjRows.length <= 2} onClick={() => setAdjRows((rows) => rows.filter((_, j) => j !== i))}>
                        移除
                      </button>
                    </div>
                  ))}
                  <div>
                    <button className="btn-ghost btn-sm" disabled={busy} onClick={() => setAdjRows((rows) => [...rows, { ...EMPTY_ADJ_ROW }])}>
                      新增分錄
                    </button>
                  </div>
                </div>
                <div className="commerce-balance">
                  <span>
                    借方合計:<span className="adm-mono">{Number.isFinite(adjBalance.debit) ? adjBalance.debit : '—'}</span>
                  </span>
                  <span>
                    貸方合計:<span className="adm-mono">{Number.isFinite(adjBalance.credit) ? adjBalance.credit : '—'}</span>
                  </span>
                  <span className={`pill ${adjBalance.balanced ? 'pill-good' : 'pill-warn'}`}>{adjBalance.balanced ? '借貸平衡' : '未平衡'}</span>
                </div>
                <div className="commerce-form-grid">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">原因</span>
                    <input value={adjReason} onChange={(e) => setAdjReason(e.target.value)} disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">案件編號</span>
                    <input value={adjCaseId} onChange={(e) => setAdjCaseId(e.target.value)} disabled={busy} />
                  </label>
                </div>
                <p className="commerce-dc-note">{DUAL_CONTROL_NOTE}</p>
                <button className="btn-danger" disabled={busy || !adjReady} onClick={() => { setErr(''); setConfirmAdj(true); }}>
                  過帳 adjustment
                </button>
                <span className="commerce-hint text-tertiary">
                  帳本不 UPDATE / DELETE:錯帳更正 = 反向分錄 + 重開正確分錄(reversal + replacement)。
                </span>
              </div>
            </section>
          )}
        </aside>
      </div>

      <CommerceApprovalPanel
        operationId={latestOperationId}
        onCompleted={() => {
          if (queriedAccount) void loadEntries(queriedAccount);
        }}
      />

      {confirmCredit && (
        <ConfirmDialog
          title="開立客戶 credit"
          tone="primary"
          confirmLabel="確認開立"
          busy={busy}
          error={err || undefined}
          warning={DUAL_CONTROL_NOTE}
          body={
            <>
              <p>
                將對 <span className="adm-mono">{creditAccount.trim()}</span> 開立 {creditType.trim()} credit{' '}
                <strong>{formatMinor(creditAmountMinor, creditCurrency)}</strong>
                {creditExpires ? `,${creditExpires.replace('T', ' ')} 到期` : '(不到期)'}。
              </p>
              <p className="text-tertiary">處置理由:{creditReason.trim()}・案件:{creditCaseId.trim()}(將寫入稽核與 ledger)</p>
            </>
          }
          onConfirm={() => void submitCredit()}
          onCancel={() => setConfirmCredit(false)}
        />
      )}

      {confirmRefund && (
        <ConfirmDialog
          title="送出退款"
          tone="danger"
          confirmLabel="確認退款"
          busy={busy}
          error={err || undefined}
          warning={`退款將即刻送金流閘道實際執行,款項退回客戶原付款方式,不可自主控台收回。${DUAL_CONTROL_NOTE}`}
          body={
            <>
              <p>
                將對付款 <span className="adm-mono">{refundPayment.trim()}</span>(帳戶 <span className="adm-mono">{refundAccount.trim()}</span>)退款{' '}
                <strong className="adm-mono">{refundAmount}</strong>(最小幣別單位,幣別依原付款)。
              </p>
              <p className="text-tertiary">處置理由:{refundReason.trim()}・案件:{refundCaseId.trim()}(將寫入稽核與 ledger)</p>
            </>
          }
          onConfirm={() => void submitRefund()}
          onCancel={() => setConfirmRefund(false)}
        />
      )}

      {confirmAdj && (
        <ConfirmDialog
          title="過帳受控調帳"
          tone="danger"
          confirmLabel="確認過帳"
          busy={busy}
          error={err || undefined}
          warning={`帳本 append-only:過帳後不可修改或刪除,錯帳需另發反向分錄更正。${DUAL_CONTROL_NOTE}`}
          body={
            <>
              <p>
                將過帳 {adjRows.length} 筆分錄(借 {adjBalance.debit} / 貸 {adjBalance.credit},{adjCurrency.trim().toUpperCase()}):{adjDescription.trim()}
              </p>
              <p className="text-tertiary">處置理由:{adjReason.trim()}・案件:{adjCaseId.trim()}(將寫入稽核)</p>
            </>
          }
          onConfirm={() => void submitAdjustment()}
          onCancel={() => setConfirmAdj(false)}
        />
      )}
    </div>
  );
}
